import { useEffect, useRef } from "react";
import type { NotificationDto, NotificationStreamEvent } from "@procureflow/shared-types";

// US-078 (ADR-022) — subscribe to the server's SSE notification stream.
//
// Opens one credentialed EventSource to GET /api/notifications/stream (the
// httpOnly auth cookie rides along automatically) and invokes `onNotification`
// for each arriving notification. EventSource auto-reconnects on drop; the
// connection is closed on unmount. Heartbeat ('ping') events are named, so they
// never reach `onmessage` and are ignored for free.
//
// `enabled` gates the connection — callers turn it off when there is no live API
// to talk to (pure-MSW dev, logged-out), since MSW does not serve SSE.
export function useNotificationStream(
  onNotification: (n: NotificationDto) => void,
  enabled = true,
): void {
  const cbRef = useRef(onNotification);
  useEffect(() => {
    cbRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof EventSource === "undefined") return;

    const es = new EventSource("/api/notifications/stream", { withCredentials: true });
    es.onmessage = (e: MessageEvent) => {
      try {
        const parsed = JSON.parse(e.data) as NotificationStreamEvent;
        if (parsed && parsed.notification) cbRef.current(parsed.notification);
      } catch {
        /* ignore malformed frames */
      }
    };
    return () => es.close();
  }, [enabled]);
}
